import { Component, DestroyRef, HostListener, inject } from '@angular/core';
import { TranslationsService } from './translations/translations.service';
import { SecurityService } from './security/services/security.service';
import { filter } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { screenType } from './lib/utils/screen.utils';
import { UserService } from 'src/app/users/user.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { environment } from 'src/environments/environment';
import { ContractState } from './utils/enums/enums/contract/contractState.enum';
import { UserLoggedInformation } from './users/model/user.model';
import { APP_ROUTES } from './app-routing.const';
import { SidebarStateFunction } from './sidebar-menu/sidebar-menu.function';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScrollLockService } from './utils/service/scroll-lock.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {

  APP_ROUTES = APP_ROUTES;

  topBarUserName: string = '';
  topBarRole: string = '';

  showPoliciesPopup: boolean = false;
  private policiesAcceptanceRequired: boolean = false;

  headerProfilePic: SafeUrl | string = './assets/logos/user/user-default.svg';

  contractState: string | undefined = ContractState.DRAFT;

  private viewportWidth: number = window.innerWidth;

  private initialized = false;

  private destroyRef = inject(DestroyRef);

  protected readonly document = document;

  constructor(protected securityService: SecurityService,
              private translationService: TranslationsService,
              protected router: Router,
              private userService: UserService,
              private sanitizer: DomSanitizer,
              protected sidebar: SidebarStateFunction,
              private scrollLockService: ScrollLockService) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        window.scrollTo({ top: 0 });
      });
  }

  ngOnInit(): void {
    this.initializeAuthState();
  }

  initializeAuthState(){
    this.securityService.authState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isLoggedIn => {
        if (isLoggedIn && !this.initialized) {
          this.initialized = true;
          this.initializeMainComponentPageData();
        }
      });
  }

  @HostListener('window:resize')
  onResize() {
    this.viewportWidth = window.innerWidth;
  }

  // =============================================
  // User / page initialization
  // =============================================

  initializeMainComponentPageData() {
    this.loadHeaderProfilePicture();

    this.userService.headerUserName$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name: string) => {
        if (name?.trim()) this.topBarUserName = name;
      });

    this.userService.headerUserRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((role: string) => {
        if (role?.trim()) this.topBarRole = role;
      });

    this.userService.getUserLoggedInformation().subscribe({
      next: (user: UserLoggedInformation) => {
        if (!user) return;
        this.userService.updateHeaderUserName(user.name);
        this.userService.updateHeaderRole(user.role);
      },
      error: () => {
        const fallbackUser = this.securityService.getLoggedUser();
        if (fallbackUser) {
          this.userService.updateHeaderUserName(
            `${fallbackUser.name || ''} ${fallbackUser.surname || ''}`.trim()
          );
        }
      }
    });
  }

  onAcceptPolicies() {
    this.userService.acceptPolicies().subscribe({
      next: () => {
        this.policiesAcceptanceRequired = false;
        this.showPoliciesPopup = false;
        this.scrollLockService.unlock();
      },
      error: (err) => console.error('Error accepting policies', err)
    });
  }

  private updatePoliciesPopupVisibility(url: string): void {
    const shouldShow = this.policiesAcceptanceRequired &&
      !APP_ROUTES.POLICIES.ROUTES.some(route => url.startsWith(route));
    console.log('shouldShow:', shouldShow, 'policiesAcceptanceRequired:', this.policiesAcceptanceRequired);
    if (shouldShow !== this.showPoliciesPopup) {
      this.showPoliciesPopup = shouldShow;
      shouldShow ? this.scrollLockService.lock() : this.scrollLockService.unlock();
    }
  }

  loadHeaderProfilePicture() {
    this.userService.getUserImageProfile().subscribe({
      next: (blob: Blob) => {
        if (blob.type === 'text/html' || blob.size === 0) return;
        this.headerProfilePic = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
      },
      error: () => console.log('No custom profile picture found, using default.')
    });
  }

  // =============================================
  // Shell visibility
  // =============================================

  showMenu(): boolean {
    return this.securityService.isLoggedIn() &&
      !(
        this.router.url.startsWith(APP_ROUTES.AUTH.LOGIN) ||
        this.router.url.startsWith(APP_ROUTES.CONTRACT.EDITOR_PREFIX) ||
        this.router.url.startsWith(APP_ROUTES.EXTERNAL.ROOT_EDITOR) ||
        this.router.url.startsWith(APP_ROUTES.TEMPLATE.ROOT_EDITOR)
      );
  }

  showHeader(): boolean {
    return this.securityService.isLoggedIn() && !this.router.url.startsWith(APP_ROUTES.AUTH.LOGIN);
  }

  showEditor(): boolean {
    return this.router.url.indexOf(APP_ROUTES.CONTRACT.EDITOR_PREFIX) >= 0;
  }

  showDevelopment(): boolean {
    return environment.development_phase === 'local';
  }

  showFooter(): boolean {
    return !this.router.url.startsWith(APP_ROUTES.CONTRACT.EDITOR_PREFIX)
        && !APP_ROUTES.POLICIES.ROUTES.some(route => this.router.url.startsWith(route));
  }

  isMobile(): boolean {
    return screenType(this.viewportWidth) === 'mobile';
  }

  getLoggedUser() {
    return this.securityService.getLoggedUser();
  }
}
